import { getData } from "Agentic Module/api/getData";
import { postData } from "Agentic Module/api/postData";
import { deleteData } from "Agentic Module/api/deleteData";

export const EMAIL_TRIAGE_BASE_URL = "https://bnlwe-af01-p-930838-unilevercom-webapp-01-bwa2huh4djcdepgd.westeurope-01.azurewebsites.net"
const FILTER_RESPONSE_KEY_RENAMES = {
  item_code: "material_code",
  item_description: "material_desc",
};

const DEFAULT_FILTER_KEYS = [
  "unit_code",
  "site_cluster",
  "material_code",
  "material_desc",
  "vendor_name",
  "format",
  "bg",
  "week",
];

function parseEmailRecipientList(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean);
  }
  const text = String(value).trim();
  if (!text) return [];
  return text
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function escapeEmailHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function createCorrelationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    const randomPart = Array.from(bytes, (b) =>
      b.toString(16).padStart(2, "0")
    ).join("");

    return `corr-${Date.now()}-${randomPart}`;
  }

  throw new Error("Secure random number generation is not available.");
}

function buildTrackingId(flat) {
  const poNumber = String(flat?.po_number ?? "").trim();
  const itemCode = String(flat?.item_code ?? "").trim();
  if (poNumber && itemCode) return `${poNumber}-${itemCode}`;
  if (poNumber || itemCode) return `${poNumber}${itemCode}`;
  return `RMPM-${Date.now()}`;
}

export function mapFiltersResponse(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const result = {};
  for (const [apiKey, value] of Object.entries(raw)) {
    if (apiKey in FILTER_RESPONSE_KEY_RENAMES) continue;
    result[apiKey] = Array.isArray(value) ? value : [];
  }
  for (const [apiKey, internalKey] of Object.entries(FILTER_RESPONSE_KEY_RENAMES)) {
    if (internalKey in result || !(apiKey in raw)) continue;
    result[internalKey] = Array.isArray(raw[apiKey]) ? raw[apiKey] : [];
  }
  DEFAULT_FILTER_KEYS.forEach((key) => {
    if (!(key in result)) result[key] = [];
  });
  return result;
}

/** Build Logic App email payload from flat draft/send form fields. */
export function buildLogicAppEmailPayload(flat = {}) {
  const body = String(flat.email_body ?? "");
  return {
    email: {
      sendAs: String(flat.email_from ?? ""),
      to: parseEmailRecipientList(flat.email_to),
      cc: parseEmailRecipientList(flat.email_cc),
      subject: String(flat.email_sub ?? "").trim() || "(no subject)",
    },
    content: {
      bodyHtml: escapeEmailHtml(body),
    },
    metadata: {
      trackingId: buildTrackingId(flat),
      requestedBy: flat.user_name,
      application: "RMPM-EmailTriage",
      correlationId: createCorrelationId(),
      timestampUtc: new Date().toISOString(),
    },
  };
}

/**
 * Outbound filter param names for `/get_filters` and `/dispatches` (and export).
 * UI state keeps `material_code` / `material_desc`; APIs expect `item_code` / `item_description`.
 */
const INTERNAL_FILTER_KEY_TO_API_PARAM = {
  material_code: "item_code",
  material_desc: "item_description",
};

function filterKeyToApiParamKey(internalKey) {
  if (internalKey == null || internalKey === "") return internalKey;
  const k = String(internalKey).trim();
  const lookup = k.toLowerCase();
  return INTERNAL_FILTER_KEY_TO_API_PARAM[lookup] ?? INTERNAL_FILTER_KEY_TO_API_PARAM[k] ?? k;
}

/** Append filter selections as query params (same shape as dispatches `filters`). */
export function appendEmailTriageFilterParams(params, filters) {
  if (!filters || typeof filters !== "object") return;
  Object.entries(filters).forEach(([key, value]) => {
    const apiKey = filterKeyToApiParamKey(key);
    if (value == null) return;
    if (Array.isArray(value)) {
      value
        .filter((v) => v != null && String(v).trim() !== "")
        .forEach((v) => params.append(apiKey, String(v)));
      return;
    }
    const v = String(value).trim();
    if (v !== "") params.set(apiKey, v);
  });
}

/** Append `sort=field:asc` query params (supports multiple sort keys). */
export function appendEmailTriageSortParams(params, sort) {
  if (!params || !sort) return;
  const entries = Array.isArray(sort) ? sort : [sort];
  entries.forEach((entry) => {
    if (!entry?.key) return;
    const direction = entry.direction === "desc" ? "desc" : "asc";
    params.append("sort", `${entry.key}:${direction}`);
  });
}

export function buildDispatchesUrl(baseUrl, { page, pageSize, filters, sort } = {}) {
  const params = new URLSearchParams();
  if (page != null) params.set("page", String(page));
  if (pageSize != null) params.set("page_size", String(pageSize));
  appendEmailTriageFilterParams(params, filters);
  appendEmailTriageSortParams(params, sort);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  return `${baseUrl}/api/v1/dispatches${queryString}`;
}

export function buildHITLUrl(baseUrl, { page, pageSize, filters, sort } = {}) {
  const params = new URLSearchParams();
  if (page != null) params.set("page", String(page));
  if (pageSize != null) params.set("page_size", String(pageSize));
  appendEmailTriageFilterParams(params, filters);
  appendEmailTriageSortParams(params, sort);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  return `${baseUrl}/api/v1/email-triage/hitl/filtered-data${queryString}`;
}

function buildDispatchesV2Url(baseUrl, { page, pageSize, filters, sort } = {}) {
  const params = new URLSearchParams();
  if (page != null) params.set("page", String(page));
  if (pageSize != null) params.set("page_size", String(pageSize));
  appendEmailTriageFilterParams(params, filters);
  appendEmailTriageSortParams(params, sort);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  return `${baseUrl}/api/v1/dispatches/v2${queryString}`;
}

/** Append `site_code` query params (string or string[]). */
function appendSiteCodeParams(params, siteCode) {
  if (siteCode == null) return;
  if (Array.isArray(siteCode)) {
    siteCode
      .filter((v) => v != null && String(v).trim() !== "")
      .forEach((v) => params.append("site_code", String(v)));
    return;
  }
  const v = String(siteCode).trim();
  if (v !== "") params.set("site_code", v);
}

/**
 * GET `/api/v1/get_kpis` — passes selected filters as query params (e.g. `site_cluster=Haridwar`).
 * @param {Record<string, string | string[]>} [filters]
 */
export async function emailTriageGetKpis(filters) {
  const params = new URLSearchParams();
  appendEmailTriageFilterParams(params, filters);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  return getData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/get_kpis/v2${queryString}`);
}

/**
 * GET /get_filters — optional `filters` adds query params (e.g. `?unit_code=UTH`) so option lists update from the API.
 */
export async function emailTriageGetFilters(filters) {
  const params = new URLSearchParams();
  appendEmailTriageFilterParams(params, filters);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  const url = `${EMAIL_TRIAGE_BASE_URL}/api/v1/get_filters${queryString}`;
  const data = await getData(url);
  return mapFiltersResponse(data);
}

function pickFiniteNumber(...values) {
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function pickIdList(...values) {
  for (const v of values) {
    if (Array.isArray(v)) return v.map((id) => String(id));
  }
  return [];
}

/**
 * Read mail counts from row root, `mailRefs`, or `lastCommunication` blobs.
 * Supports flat (`inboxMailCount`) and nested v2 (`mailRefs.replyMailCount`) shapes.
 */
export function extractDispatchMailCounts(item) {
  if (!item || typeof item !== "object") {
    return {
      inboxMailCount: 0,
      replyMailCount: 0,
      inboxMailIds: [],
      replyMailIds: [],
    };
  }

  const refs = item.mailRefs ?? item.mail_refs;
  const mailRefs = refs && typeof refs === "object" && !Array.isArray(refs) ? refs : {};
  const lastComm = item.lastCommunication ?? item.last_communication;
  const lastCommObj =
    lastComm && typeof lastComm === "object" && !Array.isArray(lastComm) ? lastComm : {};

  const replyMailCount = pickFiniteNumber(
    item.replyMailCount,
    item.reply_mail_count,
    mailRefs.replyMailCount,
    mailRefs.reply_mail_count,
    lastCommObj.replyMailCount,
    lastCommObj.reply_mail_count,
    item.emailsSentCount,
    item.emails_sent,
    item.sentCount,
  );

  const inboxMailCount = pickFiniteNumber(
    item.inboxMailCount,
    item.inbox_mail_count,
    item.inboundMailCount,
    item.inbound_mail_count,
    mailRefs.inboxMailCount,
    mailRefs.inbox_mail_count,
    mailRefs.inboundMailCount,
    mailRefs.inbound_mail_count,
    lastCommObj.inboxMailCount,
    lastCommObj.inbox_mail_count,
    item.emailsReceivedCount,
    item.emails_received,
    item.receivedCount,
  );

  const replyMailIds = pickIdList(
    item.replyMailIds,
    item.reply_mail_ids,
    mailRefs.replyMailIds,
    mailRefs.reply_mail_ids,
    lastCommObj.replyMailIds,
    lastCommObj.reply_mail_ids,
  );

  const inboxMailIds = pickIdList(
    item.inboxMailIds,
    item.inbox_mail_ids,
    mailRefs.inboxMailIds,
    mailRefs.inbox_mail_ids,
    lastCommObj.inboxMailIds,
    lastCommObj.inbox_mail_ids,
  );

  return { inboxMailCount, replyMailCount, inboxMailIds, replyMailIds };
}

/** Normalize mail counts/ids from `/dispatches` and `/dispatches/v2` row payloads. */
export function normalizeDispatchRow(item, vendorMailFallback = null) {
  if (!item || typeof item !== "object") return item;

  let itemMailRefs = {};

if (item.mailRefs && typeof item.mailRefs === "object" && !Array.isArray(item.mailRefs)) {
  itemMailRefs = item.mailRefs;
} else if (
  item.mail_refs &&
  typeof item.mail_refs === "object" &&
  !Array.isArray(item.mail_refs)
) {
  itemMailRefs = item.mail_refs;
}

const merged = vendorMailFallback
  ? {
      ...vendorMailFallback,
      ...item,
      mailRefs: {
        ...(vendorMailFallback.mailRefs &&
        typeof vendorMailFallback.mailRefs === "object" &&
        !Array.isArray(vendorMailFallback.mailRefs)
          ? vendorMailFallback.mailRefs
          : {}),
        ...itemMailRefs,
      },
    }
  : item;

  const mail = extractDispatchMailCounts(merged);

  return {
    ...merged,
    supplierInventory:
      merged.supplier_inventory ??
      merged.supplierInventory ??
      null,
    mailRefs:
      merged.mailRefs ??
      merged.mail_refs ??
      (vendorMailFallback?.mailRefs || vendorMailFallback?.mail_refs) ??
      undefined,
    inboxMailCount: mail.inboxMailCount,
    replyMailCount: mail.replyMailCount,
    inboxMailIds: mail.inboxMailIds,
    replyMailIds: mail.replyMailIds,
    emailsReceivedCount: mail.inboxMailCount,
    emailsSentCount: mail.replyMailCount,
  };
}

function extractDispatchItemsFromResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.dispatches)) return data.dispatches;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export function normalizeDispatchesListResponse(data) {
  if (!data || typeof data !== "object") return data;
  const items = extractDispatchItemsFromResponse(data).map((row) =>
    normalizeDispatchRow(row),
  );
  return { ...data, items };
}

export async function emailTriageGetDispatches({ page, pageSize, filters, sort } = {}) {
  const data = await getData(
    buildDispatchesUrl(EMAIL_TRIAGE_BASE_URL, { page, pageSize, filters, sort }),
  );
  return normalizeDispatchesListResponse(data);
}

function resolveVendorName(item, fallbackVendorName = "") {
  const vendorFields = [
    item?.vendorName,
    item?.vendor_name,
    item?.supplierName,
    item?.supplier_name,
    item?.["Supplier name"],
  ];

  const resolved = vendorFields.find(
    (value) =>
      value != null &&
      String(value).trim() !== "",
  );

  return resolved || fallbackVendorName;
}

function normalizeVendorDispatchItem(
  item,
  vendorName,
  vendorMailFallback,
) {
  return normalizeDispatchRow(
    {
      ...item,
      vendorName: resolveVendorName(
        item,
        vendorName,
      ),
    },
    vendorMailFallback,
  );
}

/**
 * GET /dispatches/v2 — vendor-grouped variant. Response shape:
 *   { vendors: [{ vendorName, items: [...] }], page, pageSize, total, totalPages }
 * We normalize it to the same `{ items, total, totalPages, page, pageSize }`
 * shape consumed by the table so the rest of the pipeline (sorting, grouping,
 * pagination UI) does not need a second code path.
 */
export async function emailTriageGetDispatchesV2({ page, pageSize, filters, sort } = {}) {
  const data = await getData(
    buildDispatchesV2Url(EMAIL_TRIAGE_BASE_URL, { page, pageSize, filters, sort }),
  );
  if (!data || typeof data !== "object") return data;
  const vendors = Array.isArray(data.vendors) ? data.vendors : [];
  const items = vendors.flatMap((v) => {
    const vendorName =
      v?.vendorName ??
      v?.vendor_name ??
      v?.supplierName ??
      v?.supplier_name ??
      v?.["Supplier name"] ??
      "";
    const list = Array.isArray(v?.items) ? v.items : [];
    const vendorMailFallback = {
      inboxMailCount: v?.inboxMailCount ?? v?.inbox_mail_count,
      replyMailCount: v?.replyMailCount ?? v?.reply_mail_count,
      inboxMailIds: v?.inboxMailIds ?? v?.inbox_mail_ids,
      replyMailIds: v?.replyMailIds ?? v?.reply_mail_ids,
      mailRefs: v?.mailRefs ?? v?.mail_refs,
    };

    return list.map((item) =>
      normalizeVendorDispatchItem(
        item,
        vendorName,
        vendorMailFallback,
      ),
    );
  });
  return {
    items,
    total: Number(data.total) || items.length,
    totalPages: Number(data.totalPages) || 0,
    page: Number(data.page) || page || 1,
    pageSize: Number(data.pageSize) || pageSize || items.length,
  };
}

/** GET /api/v1/email_history?vendor_name=… — history is scoped by vendor. */
export async function emailTriageGetEmailHistory(vendorName) {
  const v = vendorName != null ? String(vendorName).trim() : "";
  const params = new URLSearchParams();
  if (v !== "") params.set("vendor_name", v);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  return getData(
    `${EMAIL_TRIAGE_BASE_URL}/api/v1/email_history${queryString}`,
  );
}

export async function emailTriagePostProcessMailV2(payload) {
  return postData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/process_mail/v2`, payload);
}

export async function emailTriagePostMailAction(payload) {
  return postData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/mail_action`, payload);
}

export async function emailTriageDeleteMail(emailId) {
  return deleteData(
    `${EMAIL_TRIAGE_BASE_URL}/api/v1/delete_mail?email_id=${encodeURIComponent(String(emailId))}`,
  );
}

export async function emailTriageGetUserPrefs(email, screenId) {
  return getData(
    `${EMAIL_TRIAGE_BASE_URL}/api/v1/user-prefs?user_email=${encodeURIComponent(email)}&screen_id=${encodeURIComponent(screenId)}`,
  );
}

export async function emailTriageSaveUserPrefs(payload) {
  return postData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/user-prefs`, payload);
}

export async function emailTriageExportData(filters) {
  const payload = {};
  if (filters && typeof filters === "object") {
    Object.entries(filters).forEach(([key, value]) => {
      if (!Array.isArray(value) || value.length === 0) return;
      payload[filterKeyToApiParamKey(key)] = value;
    });
  }
  return postData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/export_data`, payload);
}
